import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Wrench, Trash2 } from 'lucide-react';

export default function Subappalti() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [cantieri, setCantieri] = useState([]);
    const [openForm, setOpenForm] = useState(false);
    const [form, setForm] = useState({
        nome: '', azienda: '', piva: '', cantiere_id: '', lavorazione: '', importo: 0, note: '',
    });

    const load = async () => {
        const [s, c] = await Promise.all([api.get('/subappalti/list'), api.get('/cantieri/list')]);
        setItems(s.data);
        setCantieri(c.data);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.nome || !form.azienda || !form.lavorazione) { toast.error(t('pages.sub_toast_fill')); return; }
        await api.post('/subappalti/create', form);
        toast.success(t('pages.sub_toast_added'));
        setOpenForm(false);
        setForm({ nome: '', azienda: '', piva: '', cantiere_id: '', lavorazione: '', importo: 0, note: '' });
        load();
    };

    const updateProgress = async (id, avanzamento) => {
        await api.put(`/subappalti/${id}`, { avanzamento });
        load();
    };

    const remove = async (id) => {
        if (!window.confirm(t('common.confirm_delete'))) return;
        await api.delete(`/subappalti/${id}`);
        load();
    };

    return (
        <div data-testid="subappalti-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl">{t('pages.sub_title')}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.sub_subtitle')}</p>
                </div>
                <Button onClick={() => setOpenForm(true)} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="subappalti-new-button"><Plus className="w-4 h-4" /> {t('pages.sub_new')}</Button>
            </div>

            <Card className="overflow-x-auto">
                {items.length === 0 ? (
                    <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                        <Wrench className="w-12 h-12 mx-auto mb-3" />
                        <p>{t('pages.sub_empty')}</p>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-[hsl(var(--muted))]">
                            <tr className="text-left">
                                <th className="p-3">{t('pages.sub_col_ref')}</th>
                                <th className="p-3">{t('pages.sub_col_company')}</th>
                                <th className="p-3 hidden md:table-cell">{t('pages.sub_col_work')}</th>
                                <th className="p-3">{t('pages.sub_col_progress')}</th>
                                <th className="p-3 text-right">{t('pages.sub_col_amount')}</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} className="border-t" data-testid={`subappalto-row-${s.id}`}>
                                    <td className="p-3 font-semibold">{s.nome}</td>
                                    <td className="p-3">{s.azienda}<br/><span className="text-xs text-[hsl(var(--muted-foreground))]">{s.piva}</span></td>
                                    <td className="p-3 hidden md:table-cell">{s.lavorazione}</td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2">
                                            <input type="range" min="0" max="100" value={s.avanzamento || 0} onChange={(e) => updateProgress(s.id, parseInt(e.target.value))} className="w-24 accent-[hsl(var(--accent))]" />
                                            <span className="font-mono text-xs w-10">{s.avanzamento || 0}%</span>
                                        </div>
                                    </td>
                                    <td className="p-3 text-right font-display text-lg tabular-nums">€{s.importo?.toLocaleString('it-IT') || 0}</td>
                                    <td className="p-3 text-right"><button onClick={() => remove(s.id)} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <Dialog open={openForm} onOpenChange={setOpenForm}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-display text-2xl">{t('pages.sub_form_title')}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div><Label>{t('pages.sub_form_ref_name')}</Label><Input value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} className="mt-1" data-testid="subappalto-nome" /></div>
                        <div><Label>{t('pages.sub_form_company')}</Label><Input value={form.azienda} onChange={(e) => setForm({...form, azienda: e.target.value})} className="mt-1" /></div>
                        <div><Label>{t('pages.sub_form_piva')}</Label><Input value={form.piva} onChange={(e) => setForm({...form, piva: e.target.value})} className="mt-1" /></div>
                        <div>
                            <Label>{t('pages.sub_form_cantiere')}</Label>
                            <Select value={form.cantiere_id} onValueChange={(v) => setForm({...form, cantiere_id: v})}>
                                <SelectTrigger className="mt-1"><SelectValue placeholder={t('pages.sub_form_cantiere_select')} /></SelectTrigger>
                                <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>{t('pages.sub_form_work')}</Label><Input value={form.lavorazione} onChange={(e) => setForm({...form, lavorazione: e.target.value})} className="mt-1" /></div>
                        <div><Label>{t('pages.sub_form_amount')}</Label><Input type="number" value={form.importo} onChange={(e) => setForm({...form, importo: parseFloat(e.target.value) || 0})} className="mt-1" /></div>
                        <Button onClick={create} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] h-11" data-testid="subappalto-submit">{t('pages.sub_form_submit')}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
